// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Reading a tile's art off the wire (M8-79).
//
// `GET /machines/{id}/versions/{semver}/art` answers with the four fields a thumbnail draws. This
// turns that answer into an `AttractArt` or into nothing, and it is a separate module from the
// component that asks for one for the reason `boardToOpen` and `screenOnPlunge` are: a decision that
// needs a browser to state is a decision nobody runs.
//
// **Absence rather than an exception, everywhere.** A tile whose art will not parse shows its name,
// which is what every tile does today. Throwing here would take `/arcade` down over a thumbnail.
//
// The parse is deliberately narrow. It reads what the painter reads and drops the rest, so a field
// added to the route later cannot reach `AttractPainter` without somebody adding it here first —
// the additive-within-major policy in 03 §5 arriving at a client that has to keep working.

import type { AttractArt, AttractDevice, AttractLamp } from './attractPainter'
import type { LampKind, Point2 } from '@core/machine'

const LAMP_KINDS: readonly string[] = ['insert', 'gi', 'flasher']

const objectOf = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined

const numberOf = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

/** A playfield coordinate pair. Anything else is not a position, and a lamp without one is skipped. */
const pointOf = (value: unknown): Point2 | undefined => {
  if (!Array.isArray(value) || value.length !== 2) return undefined
  const x = numberOf(value[0])
  const y = numberOf(value[1])
  return x === undefined || y === undefined ? undefined : [x, y]
}

/**
 * One lamp, or nothing.
 *
 * `colour` is the only field the drawing cannot do without — it is what the arc is filled with —
 * so a lamp that does not state one is dropped rather than given a default. A made-up colour is a
 * picture of a machine that does not exist, which is the objection this painter's own header makes
 * about inventing anything at all.
 */
function lampOf(value: unknown): AttractLamp | undefined {
  const row = objectOf(value)
  if (row === undefined) return undefined
  const colour = row['colour']
  if (typeof colour !== 'string' || colour === '') return undefined
  const kind = typeof row['kind'] === 'string' && LAMP_KINDS.includes(row['kind']) ? (row['kind'] as LampKind) : undefined
  const position = pointOf(row['position'])
  const size = pointOf(row['size'])
  const radius = numberOf(row['radius'])
  return {
    colour,
    ...(kind === undefined ? {} : { kind }),
    ...(position === undefined ? {} : { position }),
    ...(size === undefined ? {} : { size }),
    ...(radius === undefined ? {} : { radius }),
  }
}

/**
 * The geometry fields a mechanism is drawn from (M8-82).
 *
 * The route sends each device narrowed to these, under the definition's **own** field names, so a
 * device off the wire and a device out of the bundled flagship are one shape to `partOf`. Copying
 * by an allowlist rather than passing the object through is what keeps that true in the other
 * direction too: a field the route starts sending — a coil, a switch, a strength — cannot reach the
 * painter because somebody widened the server.
 */
const DEVICE_FIELDS: readonly string[] = [
  'pivot',
  'length',
  'restAngle',
  'a',
  'b',
  'centre',
  'skirtRadius',
  'captureRadius',
  'region',
]

/**
 * One mechanism, or nothing.
 *
 * `kind` is the field the drawing cannot do without — it is what decides whether a thing is a bat,
 * a hole or a lit ring — so a device that does not state one is dropped. The rest are copied as
 * they are and validated by `partOf` at the moment it draws them, which is where the shapes are
 * known; validating them twice, differently, is how two readers come to disagree.
 */
function deviceOf(value: unknown): AttractDevice | undefined {
  const row = objectOf(value)
  if (row === undefined) return undefined
  const kind = row['kind']
  if (typeof kind !== 'string' || kind === '') return undefined
  const one: Record<string, unknown> = { kind }
  for (const field of DEVICE_FIELDS) {
    if (row[field] !== undefined) one[field] = row[field]
  }
  return one as AttractDevice
}

/**
 * The art payload as something the painter will accept, or nothing.
 *
 * **`dimensions.playfield` is the one required field**, and it is required because the insert
 * arithmetic divides by its extents: a missing width is every lamp at infinity, and a zero width is
 * every lamp at `NaN`. The art program itself is optional — a machine whose author has not drawn a
 * playfield yet still has lamps, and a tile showing its lamps over a blank surface is a truer
 * picture of that machine than no tile art at all.
 */
export function parseAttractArt(value: unknown): AttractArt | undefined {
  const row = objectOf(value)
  if (row === undefined) return undefined
  const playfield = objectOf(objectOf(row['dimensions'])?.['playfield'])
  const width = numberOf(playfield?.['width'])
  const length = numberOf(playfield?.['length'])
  if (width === undefined || length === undefined || width <= 0 || length <= 0) return undefined

  const art = objectOf(row['art'])
  const surface = objectOf(art?.['playfield'])
  const backglass = objectOf(art?.['backglass'])
  const flanks = objectOf(art?.['cabinet'])
  const seed = numberOf(art?.['seed'])
  const incline = numberOf(objectOf(row['dimensions'])?.['inclineDegrees'])
  const barHeight = numberOf(objectOf(row['dimensions'])?.['lockdownBarHeight'])
  const body = objectOf(objectOf(row['dimensions'])?.['cabinet'])
  const lamps: AttractLamp[] = []
  for (const lamp of Array.isArray(row['lamps']) ? row['lamps'] : []) {
    const one = lampOf(lamp)
    if (one !== undefined) lamps.push(one)
  }
  const devices: AttractDevice[] = []
  for (const device of Array.isArray(row['devices']) ? row['devices'] : []) {
    const one = deviceOf(device)
    if (one !== undefined) devices.push(one)
  }
  // **Passed through rather than parsed** (M8-84). These three reach `world/rails.ts`,
  // `world/ramps.ts` and `world/toys.ts` — the game's own builders — and every one of them
  // already reports what it could not draw rather than throwing at it, which is a validation
  // this file would otherwise be writing a second, differing copy of. The array-ness is checked,
  // because that is what the loops below rely on and what a `jsonb` column does not promise.
  const layers = Array.isArray(row['layers']) ? (row['layers'] as unknown[]) : []
  const colliders = Array.isArray(row['colliders']) ? (row['colliders'] as unknown[]) : []
  const toys = Array.isArray(row['toys']) ? (row['toys'] as unknown[]) : []

  return {
    dimensions: {
      playfield: { width, length },
      // Each optional, and each dropped rather than defaulted when it is not a number. A default
      // invented here would be a machine drawn to somebody else's proportions; `cabinet.ts` already
      // falls back to a standard Williams-era body, which is one place to keep that decision.
      ...(incline === undefined ? {} : { inclineDegrees: incline }),
      ...(barHeight === undefined ? {} : { lockdownBarHeight: barHeight }),
      ...(body === undefined
        ? {}
        : {
            cabinet: {
              ...(numberOf(body['length']) === undefined ? {} : { length: numberOf(body['length'])! }),
              ...(numberOf(body['width']) === undefined ? {} : { width: numberOf(body['width'])! }),
              ...(numberOf(body['height']) === undefined ? {} : { height: numberOf(body['height'])! }),
            },
          }),
    },
    // Cast at the boundary, once, for the reason `prepare` casts on the way in: the art program is
    // a document the renderer walks op by op and validates as it goes, and re-implementing that
    // validation here would be a second opinion about a format that already has one.
    // **Every drawn surface, since M8-81.** A tile shows the machine rather than its lid, so the
    // backglass and both flanks come through beside the playfield. Kept only when at least one of
    // them is really there: an `art` key holding nothing tells the renderer it has something.
    ...(surface === undefined && backglass === undefined && flanks === undefined && seed === undefined
      ? {}
      : {
          art: {
            ...(surface === undefined ? {} : { playfield: surface }),
            ...(backglass === undefined ? {} : { backglass }),
            ...(flanks === undefined
              ? {}
              : {
                  cabinet: {
                    ...(objectOf(flanks['left']) === undefined ? {} : { left: flanks['left'] }),
                    ...(objectOf(flanks['right']) === undefined ? {} : { right: flanks['right'] }),
                  },
                }),
            ...(seed === undefined ? {} : { seed }),
          } as AttractArt['art'],
        }),
    lamps,
    devices,
    layers,
    colliders,
    toys,
  }
}
